import { useEffect, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { splitDescription } from '@/modules/library/utils/storyAttributionUtils';
import { isTemplateDifficulty } from '@/components/publish-flow/templateOptions';
import type { TemplateDifficulty } from '@/components/publish-flow/types';
import { TEMPLATE_COVER_ORDER } from '@/modules/uiImages/types';
import type { UIImage } from '@/modules/uiImages/types';
import { fetchStoryRow } from '@/modules/databaseService/scenes';

export interface SubmittedTemplateDetails {
  description: string;
  category: string;
  difficulty: TemplateDifficulty | '';
  tags: string[];
  coverUrl: string | null;
  extraImageUrls: string[];
  videoUrl: string | null;
}

// Reads the listing as the gallery and the reviewers see it. Everything is on the
// template copy's own origin: the submit path writes the metadata there and
// mirrors the media across, so the source story is free to diverge afterwards.
export function useSubmittedTemplateDetails(
  templateSceneOriginId: string | null | undefined,
): SubmittedTemplateDetails | null {
  const services = useServices();
  const [details, setDetails] = useState<SubmittedTemplateDetails | null>(null);

  // A new template drops the old one's details until its own read lands
  const [prevRequest, setPrevRequest] = useState({ templateSceneOriginId, services });
  if (prevRequest.templateSceneOriginId !== templateSceneOriginId || prevRequest.services !== services) {
    setPrevRequest({ templateSceneOriginId, services });
    setDetails(null);
  }

  useEffect(() => {
    if (!templateSceneOriginId) return;
    let cancelled = false;
    void (async () => {
      const [originRes, imagesRes] = await Promise.all([
        fetchStoryRow(templateSceneOriginId),
        services.uiImages.getUIImagesForSceneOrigin(templateSceneOriginId, true),
      ]);
      if (cancelled) return;

      // A failed read leaves the story's fields empty, as before
      const row = originRes.data;
      const images = imagesRes.success && imagesRes.data ? imagesRes.data : [];
      const difficulty = String(row?.scene_origin_difficulty ?? '');
      const templateLandscapes = byCategory(images, 'TemplateLandscape');
      const ownCover = templateLandscapes.find(
        (img) => img.imageOrderNumber === TEMPLATE_COVER_ORDER,
      );

      setDetails({
        description: splitDescription(String(row?.scene_origin_description ?? '')).userText,
        category: (row?.scene_origin_category as string) ?? '',
        difficulty: isTemplateDifficulty(difficulty) ? difficulty : '',
        tags: (row?.scene_origin_tags as string[]) ?? [],
        coverUrl: displayUrl(ownCover) ?? displayUrl(byCategory(images, 'SceneLandscape')[0]),
        extraImageUrls: templateLandscapes
          .filter((img) => img.imageOrderNumber !== TEMPLATE_COVER_ORDER)
          .map(displayUrl)
          .filter((url): url is string => !!url),
        videoUrl: byCategory(images, 'SceneVideo')[0]?.imageOriginalUrl ?? null,
      });
    })().catch(() => {
      if (!cancelled) setDetails(null);
    });
    return () => {
      cancelled = true;
    };
  }, [templateSceneOriginId, services]);

  return details;
}

function byCategory(images: UIImage[], category: string): UIImage[] {
  return images
    .filter((img) => img.imageCategory === category)
    .sort((a, b) => (a.imageOrderNumber || 0) - (b.imageOrderNumber || 0));
}

function displayUrl(image: UIImage | undefined): string | null {
  return image ? image.imageCroppedUrl || image.imageOriginalUrl || null : null;
}
